import { StatusPill } from "@/components/iris";
import type { ReactNode } from "react";

/**
 * IR-1548: every scraping session that touched one group, prod and dev
 * INTERLEAVED and sorted by time.
 *
 * Interleaved rather than grouped by environment on purpose: a session present
 * in prod and absent from dev then shows as a visible gap in the sequence,
 * which is the diagnostic this table exists for. Grouped, both lists look
 * healthy.
 *
 * ⚠️ The rows arrive ALREADY MERGED from the backend (`env=both`). Never merge
 * two independently-paginated lists here: concatenating them drops and
 * duplicates rows silently.
 *
 * ── House style ───────────────────────────────────────────────────────────
 * This used to be a hand-rolled `<table className="w-full text-sm">` with four
 * hard-coded hex outcome colours and an expand/collapse TEXT button trailing
 * the row, which is exactly why it did not look like the rest of Ops: it used
 * none of the house styles while ten other feature tabs do.
 *
 * It now follows the Avatars tab, which is the house pattern for an expandable
 * table — and deliberately NOT the shared DataTable: `avatars-tab.tsx` states
 * it "can't use the shared DataTable" because of the expand toggle. So:
 * `iris-table` for the type scale and row rhythm, a caret LEADING the row, one
 * pair open at a time owned by the PARENT, and the expander as a full-span cell
 * holding its own component.
 *
 * ── Prod and dev are twins ────────────────────────────────────────────────
 * The two rows for one `cycle_id` are the same session in two environments, and
 * their adjacency is the whole point of the interleaved sort. So the PAIR
 * expands as a unit, below both rows — a detail row inserted between them would
 * push the twin away by the height of the panel, and a missing twin would stop
 * reading as a gap.
 */
export type GroupSessionRow = {
  env: string;
  cycle_id: string;
  group_folder: string | null;
  avatar_name: string | null;
  platform: string | null;
  /** ⚠️ SESSION START, not when this group was collected. */
  collected_at: string | null;
  ingested_at: string | null;
  outcome: string;
  retrievable: boolean;
};

/** Outcome vocabulary, matching the backend's `_outcome()` exactly. */
const OUTCOME_LABELS: Record<string, string> = {
  data: "data",
  empty: "visited, empty",
  miss: "attempted, no data",
  unknown: "unknown (attempt unknown)",
  absent: "not attempted",
};

/**
 * Five outcomes, five distinct pills — no inline hex.
 *
 * IR-1753 r8: `unknown` (attempted IS NULL) takes its own purple pill, never
 * `idle`. "We do not know whether a read started" and "we never tried" are
 * different facts. A word this table has never seen also gets the `unknown`
 * pill (with the raw word as its label), not the idle grey that would make it
 * look like an absence.
 *
 * `empty` takes `info` (blue) rather than sharing `idle` grey with `absent`:
 * "we looked and the group had nothing" and "we never tried" are different
 * facts, and collapsing them loses the one distinction this table is for.
 * `iris-status--info` was added for exactly this.
 */
const OUTCOME_VARIANT: Record<string, "ok" | "info" | "warn" | "unknown" | "idle"> = {
  data: "ok",
  empty: "info",
  miss: "warn",
  unknown: "unknown",
  absent: "idle",
};

function fmt(value: string | null): string {
  if (!value) return "--";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "--" : d.toISOString().slice(0, 16).replace("T", " ");
}

/** Rows grouped into their prod/dev pairs, preserving the incoming order. */
function toPairs(rows: GroupSessionRow[]): Array<{ cycleId: string; rows: GroupSessionRow[] }> {
  const order: string[] = [];
  const byCycle = new Map<string, GroupSessionRow[]>();
  for (const r of rows) {
    if (!byCycle.has(r.cycle_id)) {
      byCycle.set(r.cycle_id, []);
      order.push(r.cycle_id);
    }
    byCycle.get(r.cycle_id)?.push(r);
  }
  return order.map((cycleId) => ({ cycleId, rows: byCycle.get(cycleId) ?? [] }));
}

/**
 * Two independent reasons a session cannot be opened, and they mean different
 * things: the folder is deleted (past 30-day retention), or ingestion never
 * recorded which subfolder was this group's (rows written before the
 * group_folder column existed).
 */
function pairDrillable(rows: GroupSessionRow[]): boolean {
  return rows.some((r) => r.retrievable && !!r.group_folder);
}

function notOpenableReason(rows: GroupSessionRow[]): string {
  return rows.some((r) => !r.retrievable) ? "expired" : "no folder recorded";
}

const COLUMNS = 7;

export function GroupSessionsTable({
  rows,
  expandedCycleId = null,
  onToggle,
  renderDetail,
}: {
  rows: GroupSessionRow[];
  /** Which PAIR is open. Owned by the parent so the row and the panel can
   *  never disagree about what is expanded. */
  expandedCycleId?: string | null;
  onToggle?: (cycleId: string) => void;
  renderDetail?: (cycleId: string, rows: GroupSessionRow[]) => ReactNode;
}) {
  if (rows.length === 0) {
    return (
      <div className="p-6 text-center text-[var(--iris-fg-3)]">
        No sessions recorded for this group.
        <div className="mt-1 text-xs">
          The index begins 2026-09-03; nothing before that is recorded.
        </div>
      </div>
    );
  }

  return (
    <table className="iris-table iris-table--dense">
      <thead>
        <tr>
          <th style={{ width: 34 }} />
          <th>ENV</th>
          <th>SESSION FOLDER</th>
          <th>AVATAR</th>
          <th title="Session start — NOT when this group was collected">COLLECTED</th>
          <th>INGESTED</th>
          <th>OUTCOME</th>
        </tr>
      </thead>
      <tbody>
        {toPairs(rows).map(({ cycleId, rows: pairRows }) => {
          const open = expandedCycleId === cycleId;
          const drillable = pairDrillable(pairRows);
          return (
            <>
              {pairRows.map((r, i) => (
                <tr key={`${r.env}:${r.cycle_id}`}>
                  {i === 0 ? (
                    <td rowSpan={pairRows.length} style={{ verticalAlign: "middle" }}>
                      {drillable ? (
                        <button
                          type="button"
                          aria-label={open ? `Collapse ${cycleId}` : `Expand ${cycleId}`}
                          aria-expanded={open}
                          onClick={() => onToggle?.(cycleId)}
                          className="text-[var(--iris-fg-3)] hover:text-[var(--iris-fg-1)]"
                        >
                          {open ? "▾" : "▸"}
                        </button>
                      ) : (
                        <span className="text-xs text-[var(--iris-fg-4)]">
                          {notOpenableReason(pairRows)}
                        </span>
                      )}
                    </td>
                  ) : null}
                  <td className="iris-td--mono">{r.env.toUpperCase()}</td>
                  <td className="iris-td--mono">{r.cycle_id}</td>
                  <td>{r.avatar_name ?? "--"}</td>
                  <td className="iris-td--num">{fmt(r.collected_at)}</td>
                  <td className="iris-td--num">{fmt(r.ingested_at)}</td>
                  <td>
                    <StatusPill variant={OUTCOME_VARIANT[r.outcome] ?? "unknown"}>
                      {OUTCOME_LABELS[r.outcome] ?? r.outcome}
                    </StatusPill>
                  </td>
                </tr>
              ))}
              {open && renderDetail ? (
                <tr key={`${cycleId}-detail`}>
                  <td colSpan={COLUMNS} style={{ padding: 0 }}>
                    {renderDetail(cycleId, pairRows)}
                  </td>
                </tr>
              ) : null}
            </>
          );
        })}
      </tbody>
    </table>
  );
}
